import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import DocumentDialog from "@/components/DocumentDialog";
import BulkJobDialog from "@/components/BulkJobDialog";
import ImportDialog from "@/components/ImportDialog";
import NotifyDialog from "@/components/NotifyDialog";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, DotsThreeVertical, Receipt, PencilSimple, Trash, MapPin, CalendarBlank, Lightning, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const FLOW = ["unscheduled", "scheduled", "in_progress", "completed", "cancelled"];

export default function Jobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState(searchParams.get("status") || "all");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [notifyBookedJob, setNotifyBookedJob] = useState(null);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => { const s = searchParams.get("status"); if (s && FLOW.includes(s)) setFilter(s); }, [searchParams]);

  const load = () => api.listJobs(filter === "all" ? {} : { status: filter }).then(setJobs).catch(() => {});
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const handleSaved = (res) => {
    load();
    if (res && res.scheduled_date && res.status !== "completed" && res.status !== "cancelled") {
      setNotifyBookedJob(res.id);
    }
  };

  const setStatus = async (j, status) => { await api.setJobStatus(j.id, status); toast.success(`Job ${status.replace("_", " ")}`); load(); };
  const makeInvoice = async (j) => { const inv = await api.invoiceFromJob(j.id); toast.success(`Invoice ${inv.number} created`); navigate("/invoices"); };
  const remove = async (j) => { if (!window.confirm(`Delete ${j.number}?`)) return; await api.deleteJob(j.id); toast.success("Deleted"); load(); };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]">
      <PageHeader title={isAdmin ? "Jobs" : "My Jobs"} subtitle={isAdmin ? "Track and dispatch all work" : "Your assigned work"} actions={
        <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 sm:w-40" data-testid="job-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {FLOW.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && <Button variant="outline" onClick={() => api.downloadCsv("jobs").catch(() => toast.error("Export failed"))} data-testid="export-jobs-csv"><DownloadSimple size={17} className="mr-1.5" /> Export CSV</Button>}
          {isAdmin && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-jobs-button"><UploadSimple size={17} className="mr-1.5" /> Import CSV</Button>}
          {isAdmin && <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-jobs-button"><Lightning size={17} weight="fill" className="mr-1 text-[#00B4D8]" /> Bulk Jobs</Button>}
          {isAdmin && <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0B1726] hover:bg-slate-800" data-testid="add-job-button"><Plus size={18} className="mr-1" /> New Job</Button>}
        </div>
      } />

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Job #</TableHead><TableHead>Title / Client</TableHead><TableHead>Schedule</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="w-10"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {jobs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No jobs found.</TableCell></TableRow>}
            {jobs.map((j) => (
              <TableRow key={j.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`job-row-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}>
                <TableCell className="font-mono font-semibold text-[#0B1726]">{j.number}</TableCell>
                <TableCell>
                  <div className="font-semibold text-slate-900">{j.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} />{j.client_name}{j.property_address ? ` · ${j.property_address}` : ""}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{j.scheduled_date ? (<span className="inline-flex items-center gap-1"><CalendarBlank size={13} className="text-[#00B4D8]" />{j.scheduled_date} {j.scheduled_time}</span>) : <span className="text-slate-400">Not scheduled</span>}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {isAdmin ? (
                    <Select value={j.status} onValueChange={(v) => setStatus(j, v)}>
                      <SelectTrigger className="w-36 h-8 border-0 p-0 focus:ring-0" data-testid={`job-status-${j.id}`}><StatusBadge status={j.status} /></SelectTrigger>
                      <SelectContent>{FLOW.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={j.status} onValueChange={(v) => setStatus(j, v)}>
                      <SelectTrigger className="w-36 h-8 border-0 p-0 focus:ring-0" data-testid={`job-status-${j.id}`}><StatusBadge status={j.status} /></SelectTrigger>
                      <SelectContent><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="text-right font-semibold">{money(j.total)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {isAdmin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`job-menu-${j.id}`}><DotsThreeVertical size={20} weight="bold" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/jobs/${j.id}`)}><PencilSimple size={16} className="mr-2" /> Open</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => makeInvoice(j)} data-testid={`job-invoice-${j.id}`}><Receipt size={16} className="mr-2" /> Generate invoice</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => remove(j)} className="text-rose-600"><Trash size={16} className="mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DocumentDialog open={open} onClose={() => setOpen(false)} type="job" initial={editing} onSaved={handleSaved} />
      <BulkJobDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onSaved={load} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} kind="jobs" onDone={load} />
      <NotifyDialog open={!!notifyBookedJob} type="booked" jobId={notifyBookedJob} onClose={() => setNotifyBookedJob(null)} />
    </div>
  );
}
